import { yupResolver } from '@hookform/resolvers/yup'
import { Grid } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { BoxButtons } from 'src/components/BoxButton'
import { Loading } from 'src/components/Loading'
import { Stepper } from 'src/components/Stepper'
import { TextFieldComponent } from 'src/components/TextFieldComponent'
import { TextFieldMask } from 'src/components/TextFieldMask'
import DataContext from 'src/contexts/DataContext'
import { emitNotificationEvent } from 'src/events'
import { composePath } from 'src/utils'
import * as Yup from 'yup'
import * as S from './styles'

const AddressPage = () => {
  const navigate = useNavigate()

  const validationScheema = Yup.object().shape({
    postal_code: Yup.string().required('Cep é obrigatório'),
    street: Yup.string().required('Nome da rua é obrigatório'),
    number: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .nullable()
      .positive('Números negativos ou 0 não são aceitos')
      .integer('Apenas números inteiros são aceitos'),
    complemento: Yup.string(),
    neighborhood: Yup.string().required('Bairro é obrigatório'),
    city: Yup.string().required('Cidade é obrigatória'),
    state: Yup.string().required('Estado é obrigatório'),
  })

  const toasts = {
    errorToLoadCep: () =>
      emitNotificationEvent({
        id: 'errorToLoadCep',
        type: 'error',
        title: 'Ops',
        message: 'Ocorreu um erro ao consultar o CEP',
        isIndeterminate: true,
        textButton: 'Fechar',
      }),
    InvalidCep: () =>
      emitNotificationEvent({
        id: 'InvalidCep',
        type: 'error',
        title: 'Ops',
        message: 'Você precisa digitar um CEP válido!',
        isIndeterminate: true,
        textButton: 'Fechar',
      }),
    cepNotFound: () =>
      emitNotificationEvent({
        id: 'cepNotFound',
        type: 'error',
        title: 'Ops',
        message: 'CEP não encontrado!',
        isIndeterminate: true,
        textButton: 'Fechar',
      }),
  }

  const defaultValues = {
    postal_code: '',
    street: '',
    number: '',
    complemento: '',
    neighborhood: '',
    city: '',
    state: '',
  }

  const {
    control,
    formState: { errors, isValid, isDirty },
    reset,
    setValue,
    handleSubmit,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationScheema),
  })

  const [cepLoading, setCepLoading] = useState<boolean>(false)

  const { registration, setRegistration } = useContext(DataContext)
  const currentStep = registration.getStep(registration, 'address')

  const getCep = (cep: string) => {
    const translator = {
      bairro: 'neighborhood',
      logradouro: 'street',
      localidade: 'city',
      cep: 'postal_code',
      uf: 'state',
    }
    const data = cep.replace('-', '').replace('_', '')
    const addressFormDataContext = currentStep?.formData

    if (data.length === 8) {
      if (cep === addressFormDataContext?.postal_code)
        setFormData(addressFormDataContext)
      else {
        setCepLoading(true)
        fetch(`https://viacep.com.br/ws/${data}/json/`)
          .then((res) => res.json())
          .then((obj) => {
            if (obj.erro) {
              clearForm()
              toasts.cepNotFound()
            }
            Object.keys(obj)
              .filter((prop) => prop in translator)
              .forEach((prop) => {
                //@ts-ignore
                setValue(translator[prop], obj[prop])
              })
            setCepLoading(false)

            trigger(['street', 'neighborhood', 'city', 'state'])
          })
          .catch((error) => {
            setCepLoading(false)
            toasts.errorToLoadCep()
          })
      }
    } else if (data.length === 0) {
      clearForm()
    } else toasts.InvalidCep()
  }

  const setFormData = (formData: any) => {
    Object.keys(formData).forEach((field) => {
      //@ts-ignore
      setValue(field, formData[field])
    })

    reset(
      {},
      {
        keepErrors: true,
        keepDirty: false,
        keepDirtyValues: false,
        keepValues: true,
        keepDefaultValues: false,
        keepIsSubmitted: true,
        keepTouched: false,
        keepIsValid: true,
        keepSubmitCount: true,
      }
    ) // resetando alguns estados especificos do formulario
  }

  const submitForm = (data: any) => {
    if (isFormValid()) {
      if (isDirty) registration.setFormData(setRegistration, 'address', data)

      navigate(composePath('/pesquisa', `admissao/${registration.course}`))
    }
  }

  const clearForm = () => {
    Object.keys(defaultValues).forEach((prop) => {
      //@ts-ignore
      setValue(prop, defaultValues[prop])
    })
  }

  const isFormValid = () => isValid || !Object.keys(errors)?.length

  useEffect(() => {
    if (!registration.course)
      navigate(`/admissao/${localStorage.getItem('course')}`)

    if (!!currentStep?.formData) {
      setFormData(currentStep.formData)
    }
  }, [])

  return (
    <S.ContainerFlex onSubmit={handleSubmit(submitForm)}>
      <S.Content>
        <Stepper
          currentStepName={currentStep?.name}
          label={currentStep?.title}
          course={registration.course}
        />
        <S.FormTitle>Informe o endereço do(a) candidato(a)</S.FormTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Controller
              name="postal_code"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldMask
                  value={field.value}
                  onChangeMask={field.onChange}
                  onBlurMask={(event) => getCep(event.target.value)}
                  onKeyDownMask={(e) => {
                    if (e.keyCode === 13) {
                      e.preventDefault()
                      getCep(e.target.value)
                    }
                  }}
                  error={!!fieldState.error}
                  mask="99999-999"
                  label="CEP do(a) candidato(a)"
                  variant="outlined"
                />
              )}
            />
            <S.HelperText>
              {errors.postal_code && errors.postal_code.message}
            </S.HelperText>
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="street"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldComponent
                  label="Nome da rua"
                  value={field.value}
                  error={!!fieldState.error}
                  onChange={field.onChange}
                />
              )}
            />
            <S.HelperText>
              {errors.street && errors.street.message}
            </S.HelperText>
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="number"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldComponent
                  type="number"
                  label="Número"
                  value={field.value}
                  error={!!fieldState.error}
                  onChange={field.onChange}
                  onKeyDown={(e) => {
                    const re = /[0-9]/
                    if (!re.test(e.key) && e.key != 'Backspace') {
                      e.preventDefault()
                    }
                  }}
                />
              )}
            />
            <S.HelperText>
              {errors.number && errors.number.message}
            </S.HelperText>
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="neighborhood"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldComponent
                  label="Bairro"
                  value={field.value}
                  error={!!fieldState.error}
                  onChange={field.onChange}
                />
              )}
            />
            <S.HelperText>
              {errors.neighborhood && errors.neighborhood.message}
            </S.HelperText>
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="city"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldComponent
                  label="Cidade"
                  value={field.value}
                  error={!!fieldState.error}
                  onChange={field.onChange}
                />
              )}
            />
            <S.HelperText>{errors.city && errors.city.message}</S.HelperText>
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="state"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldComponent
                  label="Estado (UF)"
                  value={field.value}
                  error={!!fieldState.error}
                  onChange={field.onChange}
                />
              )}
            />
            <S.HelperText>{errors.state && errors.state.message}</S.HelperText>
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="complemento"
              control={control}
              render={({ field, fieldState }) => (
                <TextFieldComponent
                  label="Complemento"
                  value={field.value}
                  error={!!fieldState.error}
                  onChange={field.onChange}
                />
              )}
            />
          </Grid>
        </Grid>

        <BoxButtons
          isLastStep={false}
          disableNextButton={!isFormValid()}
          isLoading={false}
          onClickPrevius={() =>
            navigate(
              composePath('/dados-pessoais', `admissao/${registration.course}`)
            )
          }
        ></BoxButtons>
      </S.Content>
      <Loading isLoading={cepLoading} />
    </S.ContainerFlex>
  )
}

export { AddressPage }
